import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  describeSupplierInstallmentTrigger,
  expandSupplierBillPayments,
  installmentApprovalRequired,
  latestBillPaidEvent,
  type SupplierBill,
  type SupplierBillPaymentSlice,
} from '../../utils/supplierBills';
import './SupplierBillHover.css';

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d: Date | null | undefined) =>
  d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/**
 * Styled hover card replacing native title tooltips. Rendered in a portal at a
 * fixed position so it never clips inside row grids; flips below the anchor
 * near the top of the viewport.
 */
export function HoverTip({
  tip,
  children,
  wide,
  block,
  inModal,
}: {
  tip: React.ReactNode;
  children: React.ReactNode;
  wide?: boolean;
  /** Render the anchor as a block-level flex row (e.g. breadcrumbs). */
  block?: boolean;
  /** Sit above finance / CRM modals (those are z-index ~10100). */
  inModal?: boolean;
}) {
  const [anchor, setAnchor] = useState<{ x: number; top: number; bottom: number } | null>(null);
  const [below, setBelow] = useState(false);
  const tipRef = useRef<HTMLDivElement>(null);

  const show = (e: React.SyntheticEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const margin = wide ? 190 : 130;
    const x = Math.max(margin, Math.min(window.innerWidth - margin, r.left + r.width / 2));
    // Wide schedule cards are tall — prefer below when the row is near the header.
    setBelow(r.top < (wide ? 400 : 220));
    setAnchor({ x, top: r.top, bottom: r.bottom });
  };
  const hide = () => setAnchor(null);

  useLayoutEffect(() => {
    if (!anchor || !tipRef.current) return;
    const headerH = 56;
    if (tipRef.current.getBoundingClientRect().top < headerH + 4) {
      setBelow(true);
    }
  }, [anchor, tip]);

  return (
    <span
      className={`bp-tip-anchor${block ? ' bp-tip-anchor--block' : ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {anchor &&
        createPortal(
          <div
            ref={tipRef}
            className={[
              'bp-tip',
              wide ? 'bp-tip--wide' : '',
              inModal ? 'bp-tip--modal' : '',
              below ? 'bp-tip--below' : 'bp-tip--above',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              left: anchor.x,
              top: below ? anchor.bottom + 8 : anchor.top - 8,
              zIndex: 10300,
            }}
            role="tooltip"
          >
            {tip}
          </div>,
          document.body,
        )}
    </span>
  );
}

/** Structured payment-schedule hover card — one block per installment. */
export function ScheduleTipContent({
  bill,
  slices,
}: {
  bill: SupplierBill;
  slices: SupplierBillPaymentSlice[];
}) {
  const terms = bill.paymentTerms;
  if (!terms || slices.length === 0) {
    return (
      <div className="bp-tip-schedule">
        <div className="bp-tip-payment">
          <div className="bp-tip-payment-head">
            <strong>Single payment</strong>
            <span className="bp-tip-amt">{fmt(bill.amount)}</span>
          </div>
          <div className="bp-tip-payment-sub">
            {bill.dueDate ? `due ${fmtDate(bill.dueDate)}` : 'no due date set'}
          </div>
          <div className={`bp-tip-status ${bill.status === 'paid' ? 'bp-tip-status--paid' : 'bp-tip-status--open'}`}>
            {bill.status === 'paid' ? 'Paid' : 'Not yet paid'}
          </div>
        </div>
      </div>
    );
  }
  const approvalRequired = installmentApprovalRequired(terms);
  return (
    <div className="bp-tip-schedule">
      {slices.map((slice, pos) => {
        const idx = slice.installmentIndex!;
        const inst = terms.installments[idx];
        const paid = bill.installmentPayments?.[idx];
        const approval = bill.installmentApprovals?.[idx];
        const billPaid = latestBillPaidEvent(bill);
        let statusClass = 'bp-tip-status--open';
        let statusText = 'Not yet paid';
        if (paid) {
          statusClass = 'bp-tip-status--paid';
          statusText = `Paid ${fmtDate(paid.paidAt)}${paid.byUserName ? ` · ${paid.byUserName}` : ''}`;
        } else if (bill.status === 'paid') {
          statusClass = 'bp-tip-status--paid';
          statusText = billPaid
            ? `Paid ${fmtDate(billPaid.at)}${billPaid.byUserName ? ` · ${billPaid.byUserName}` : ''}`
            : 'Paid';
        } else if (approvalRequired && approval) {
          statusClass = 'bp-tip-status--approved';
          statusText = `Approved ${fmtDate(approval.approvedAt)}${approval.byUserName ? ` · ${approval.byUserName}` : ''} — process payment`;
        } else if (approvalRequired) {
          statusClass = 'bp-tip-status--waiting';
          statusText = 'Awaiting approval';
        }
        return (
          <div className="bp-tip-payment" key={idx}>
            <div className="bp-tip-payment-head">
              <strong>
                Payment {pos + 1} of {slices.length}
              </strong>
              <span className="bp-tip-amt">
                {fmt(slice.amount)}
                <span className="bp-tip-pct"> ({Math.round(inst?.percent ?? 0)}%)</span>
              </span>
            </div>
            <div className="bp-tip-payment-sub">
              {inst ? describeSupplierInstallmentTrigger(inst) : slice.label}
              {' · '}
              {slice.dueDate
                ? `due ${fmtDate(slice.dueDate)}${inst?.trigger === 'task_complete' ? ' (tracks schedule)' : ''}`
                : 'due date TBD'}
            </div>
            <div className={`bp-tip-status ${statusClass}`}>{statusText}</div>
            {!paid && approval?.note ? <div className="bp-tip-note">{approval.note}</div> : null}
          </div>
        );
      })}
      {terms.earlyPayDiscountPercent && terms.earlyPayWithinDays ? (
        <div className="bp-tip-note">
          {terms.earlyPayDiscountPercent}% early-pay discount when paid within{' '}
          {terms.earlyPayWithinDays} days
        </div>
      ) : null}
      {terms.notes?.trim() && <div className="bp-tip-note">{terms.notes.trim()}</div>}
    </div>
  );
}

/** Resolve installment slices for a bill against live task end dates. */
export function billScheduleSlices(
  bill: SupplierBill,
  taskEndDateById?: Map<string, Date>,
): SupplierBillPaymentSlice[] {
  return expandSupplierBillPayments(bill, { taskEndDateById }).filter(
    (s) => s.installmentIndex !== undefined,
  );
}
